import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDevice, useSensorData, useProductCapabilities } from '@/hooks';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { isWaterZone, alarmTypeIconPath } from '@/lib/enum-resolvers';
import { isSwitchReserved } from '@/lib/product-capabilities';
import { computeMetrics, statusColors } from '@/lib/sensor-metrics';

// ── Card icons ──

const ICONS = {
  geofence: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  security: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  bilge: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
  dcBanks: 'M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M3.75 18h15A2.25 2.25 0 0021 15.75v-6a2.25 2.25 0 00-2.25-2.25h-15A2.25 2.25 0 001.5 9.75v6A2.25 2.25 0 003.75 18z',
  acPower: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  // Reuse the same shapes as the per-zone icons (alarmTypeIconPath) so a
  // Smoke/CO zone card on the dashboard and its individual zone tile inside
  // the panel use the same iconography.
  smokeCo: alarmTypeIconPath(4),
  temperature: alarmTypeIconPath(10),
  miscSiren: alarmTypeIconPath(31),
  misc: alarmTypeIconPath(30),
};

export function ApplicationsPage() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const { sensorData, availability } = useSensorData();
  const caps = useProductCapabilities();
  const navigate = useNavigate();

  const metrics = useMemo(() => computeMetrics(sensorData), [sensorData]);
  const switchReserved = isSwitchReserved(caps, sensorData?.securitySensorlist ?? []);

  if (deviceInfoLoading && !selectedDeviceInfo) return <PageSpinner />;

  if (!selectedDeviceInfo) {
    return <EmptyState title="No Device Selected" description="Select a device to view applications." />;
  }

  // Build cards dynamically based on available sensors
  interface AppCard {
    id: string; title: string; icon: string; route: string; available: boolean;
    status: 'normal' | 'warning' | 'danger' | 'neutral'; summary: string; detail?: React.ReactNode;
  }

  const cards: AppCard[] = [
    {
      id: 'intrusion', title: 'Intrusion Zones', icon: ICONS.security,
      route: `/device/${deviceUUID}/intrusion`,
      // Mobile only shows a category tile once ≥1 zone actually matches that
      // category's alarmType filter (DeviceInfo.cs's ActiveDeviceCategories) —
      // not just "this device has any security-sensor-list entries at all".
      available: metrics.totalZones > 0,
      status: metrics.securityStatus,
      summary: metrics.securitySummary,
      detail: metrics.totalZones > 0 ? (
        <span className="text-[10px]">{metrics.totalZones} zone{metrics.totalZones !== 1 ? 's' : ''}</span>
      ) : null,
    },
    {
      id: 'smoke-co', title: 'Smoke/CO', icon: ICONS.smokeCo,
      route: `/device/${deviceUUID}/smoke-co`,
      available: metrics.smokeCoTotal > 0,
      status: metrics.smokeCoStatus,
      summary: metrics.smokeCoAlarming > 0
        ? `${metrics.smokeCoAlarming} zone${metrics.smokeCoAlarming > 1 ? 's' : ''} alarming`
        : `${metrics.smokeCoOnline} of ${metrics.smokeCoTotal} online`,
      detail: (
        <span className="text-[10px]">{metrics.smokeCoTotal} zone{metrics.smokeCoTotal !== 1 ? 's' : ''}</span>
      ),
    },
    {
      id: 'temperature', title: 'Temperature', icon: ICONS.temperature,
      route: `/device/${deviceUUID}/temperature`,
      available: metrics.temperatureTotal > 0,
      status: metrics.temperatureStatus,
      summary: metrics.temperatureAlarming > 0
        ? `${metrics.temperatureAlarming} zone${metrics.temperatureAlarming > 1 ? 's' : ''} alarming`
        : `${metrics.temperatureOnline} of ${metrics.temperatureTotal} online`,
      detail: (
        <span className="text-[10px]">{metrics.temperatureTotal} zone{metrics.temperatureTotal !== 1 ? 's' : ''}</span>
      ),
    },
    {
      id: 'misc-siren', title: 'Misc & Siren', icon: ICONS.miscSiren,
      route: `/device/${deviceUUID}/misc-siren`,
      available: metrics.miscSirenTotal > 0,
      status: metrics.miscSirenStatus,
      summary: metrics.miscSirenAlarming > 0
        ? `${metrics.miscSirenAlarming} zone${metrics.miscSirenAlarming > 1 ? 's' : ''} alarming`
        : `${metrics.miscSirenOnline} of ${metrics.miscSirenTotal} online`,
      detail: (
        <span className="text-[10px]">{metrics.miscSirenTotal} zone{metrics.miscSirenTotal !== 1 ? 's' : ''}</span>
      ),
    },
    {
      id: 'misc', title: 'Misc', icon: ICONS.misc,
      route: `/device/${deviceUUID}/misc`,
      available: metrics.miscTotal > 0,
      status: metrics.miscStatus,
      summary: metrics.miscAlarming > 0
        ? `${metrics.miscAlarming} zone${metrics.miscAlarming > 1 ? 's' : ''} alarming`
        : `${metrics.miscOnline} of ${metrics.miscTotal} online`,
      detail: (
        <span className="text-[10px]">{metrics.miscTotal} zone{metrics.miscTotal !== 1 ? 's' : ''}</span>
      ),
    },
    {
      id: 'dc-banks', title: 'DC Banks', icon: ICONS.dcBanks,
      route: `/device/${deviceUUID}/dc-banks`,
      available: availability.hasDC,
      status: metrics.dcStatus,
      summary: metrics.dcCritical > 0
        ? `${metrics.dcCritical} critical`
        : metrics.dcAlerting > 0
          ? `${metrics.dcAlerting} alert${metrics.dcAlerting > 1 ? 's' : ''}`
          : 'All normal',
      detail: metrics.dcSummaryLines.length > 0 ? (
        <div className="mt-2 space-y-1">
          {metrics.dcSummaryLines.map((line) => (
            <div key={line.label} className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500 dark:text-slate-400">{line.label}</span>
              <span className={`font-mono font-semibold ${
                line.status === 'danger' ? 'text-red-600 dark:text-red-400'
                  : line.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-green-600 dark:text-green-400'
              }`}>{line.voltage}</span>
            </div>
          ))}
        </div>
      ) : null,
    },
    {
      id: 'ac-power', title: 'AC Shore Power', icon: ICONS.acPower,
      route: `/device/${deviceUUID}/ac-power`,
      available: availability.hasAC,
      status: metrics.acStatus,
      summary: metrics.acSummary,
    },
    {
      id: 'device-geofence', title: 'Device Geofence', icon: ICONS.geofence,
      route: `/device/${deviceUUID}/device-geofence`,
      available: availability.hasGeofence,
      status: metrics.geofenceStatus,
      summary: metrics.geofenceSummary,
    },
    {
      id: 'global-geofences', title: 'Global Geofences', icon: ICONS.geofence,
      route: `/device/${deviceUUID}/global-geofences`,
      available: true, // page handles subscription check
      // Green when inside at least one geofence, grey when outside all of
      // them (or none configured) -- a vessel is normally outside most of
      // its geofences at any moment (they're centered on different spots),
      // so "inside" means inside ANY one, not simultaneously inside all.
      status: metrics.globalGeofencesInside > 0 ? 'normal' : 'neutral',
      summary: metrics.globalGeofencesOutside > 0
        ? `${metrics.globalGeofencesOutside} outside boundary`
        : 'Manage boundaries',
    },
    {
      id: 'bilge', title: 'Bilge / Float Switch', icon: ICONS.bilge,
      route: `/device/${deviceUUID}/bilge`,
      available: (!switchReserved && availability.hasSwitches) || availability.hasNevata || metrics.hasWaterZones,
      status: metrics.bilgeStatus,
      summary: switchReserved
        ? metrics.nevataAlerting > 0
          ? metrics.bilgeSummary
          : `${(sensorData?.nevataSensorList?.length ?? 0) + (sensorData?.securitySensorlist ?? []).filter((z) => isWaterZone(z.alarmType)).length} sensor${((sensorData?.nevataSensorList?.length ?? 0) + (sensorData?.securitySensorlist ?? []).filter((z) => isWaterZone(z.alarmType)).length) !== 1 ? 's' : ''} normal`
        : metrics.bilgeSummary,
    },
  ];

  const availableCards = cards.filter((c) => c.available);

  if (availableCards.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Applications</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Monitor your vessel's sensors and systems</p>
        </div>
        <EmptyState
          title="No Applications Available"
          description="No sensor applications are active on this device. Applications will appear here when sensors are paired."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Applications</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Monitor your vessel's sensors and systems</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {availableCards.map((card) => {
          const sc = statusColors[card.status];
          return (
            <button
              key={card.id}
              onClick={() => navigate(card.route)}
              className={`group rounded-xl border ${sc.border} ${sc.bg} p-5 text-left transition-all hover:shadow-md`}
            >
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/80 dark:bg-navy-800">
                    <svg className="h-5 w-5 text-slate-600 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={card.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{card.title}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${sc.dot}`} />
                </div>
              </div>

              {/* Summary line */}
              <div className={`mt-3 text-xs font-medium ${sc.text}`}>
                {card.summary}
              </div>

              {/* Detail metrics (e.g., per-bank voltage readings) */}
              {card.detail}

              {/* Footer */}
              <div className="mt-3 flex items-center justify-between">
                {card.detail ? null : (
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Tap to view details
                  </span>
                )}
                <svg className="ml-auto h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-500 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
